import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Bot,
  Send,
  Plus,
  X,
  History,
  Square,
  KeyRound,
  AlertTriangle,
  User,
  Building2,
  Briefcase,
  FileText,
} from "lucide-react";
import { listProviderStatuses, type ProviderStatus } from "@/lib/aiProviders";
import { getAnthropicStatus, type OAuthStatus } from "@/lib/anthropicOAuth";
import { MessageContent } from "./blocks/MessageContent";
import { ToolTimeline } from "./ToolTimeline";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  useAgentChat,
  type ChatContext,
  type ChatMessage,
} from "@/hooks/useAgentChat";

const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;

function useRouteContext(): ChatContext | undefined {
  const location = useLocation();
  const params = useParams();
  return useMemo(() => {
    const path = location.pathname;
    const id = params.id;
    if (path.startsWith("/contacts/") && id) {
      return { page: path, entity_type: "contact", entity_id: id };
    }
    if (path.startsWith("/deals/") && id) {
      return { page: path, entity_type: "deal", entity_id: id };
    }
    if (path.startsWith("/companies/") && id) {
      return { page: path, entity_type: "company", entity_id: id };
    }
    if (path === "/" || path.startsWith("/dashboard")) {
      return { page: "dashboard" };
    }
    return { page: path };
  }, [location.pathname, params.id]);
}

type ConvSummary = {
  id: string;
  title: string;
  updated_at: string;
};

function useConversations(openedHistory: boolean) {
  const [items, setItems] = useState<ConvSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from("chat_conversations")
      .select("id, title, updated_at")
      .is("archived_at", null)
      .order("updated_at", { ascending: false })
      .limit(50);
    setItems((data ?? []) as ConvSummary[]);
    setLoading(false);
  }, []);
  useEffect(() => {
    if (openedHistory) void reload();
  }, [openedHistory, reload]);
  return { items, loading, reload };
}

export function AgentSidebar() {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [historyOpen, setHistoryOpen] = useState(false);
  const ctx = useRouteContext();
  const chat = useAgentChat();
  const { items } = useConversations(historyOpen);
  const [providerStatuses, setProviderStatuses] = useState<ProviderStatus[]>(
    [],
  );
  const [oauth, setOauth] = useState<OAuthStatus>({ connected: false });
  const refreshProviders = useCallback(() => {
    listProviderStatuses()
      .then(setProviderStatuses)
      .catch(() => {});
    getAnthropicStatus()
      .then(setOauth)
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (open) refreshProviders();
  }, [open, refreshProviders]);
  const connectedProviders = providerStatuses.filter((s) => s.connected);
  const hasOAuth = oauth.connected;
  const hasAnyAuth = hasOAuth || connectedProviders.length > 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number | null>(null);
  const resizeStartW = useRef<number>(width);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "l") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [chat.messages]);

  const onMouseDown = (e: React.MouseEvent) => {
    resizeStartX.current = e.clientX;
    resizeStartW.current = width;
    const onMove = (ev: MouseEvent) => {
      if (resizeStartX.current === null) return;
      const delta = resizeStartX.current - ev.clientX;
      const w = Math.max(
        MIN_WIDTH,
        Math.min(MAX_WIDTH, resizeStartW.current + delta),
      );
      setWidth(w);
    };
    const onUp = () => {
      resizeStartX.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-[50px] w-[50px] items-center justify-center rounded-full bg-[#1c1d20] text-white shadow-[0_6px_18px_rgba(0,0,0,0.18)] hover:bg-[#2a2c30] transition-colors"
        aria-label="Ouvrir l'assistant (Ctrl+L)"
        title="Assistant (Ctrl+L)"
      >
        <Bot className="h-5 w-5" />
      </button>
    );
  }

  return (
    <aside
      className="fixed right-0 top-0 z-40 flex h-screen flex-col border-l border-[#e8e7e4] bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.06)] animate-[agent-slidein_0.25s_ease-out]"
      style={{ width }}
    >
      <div
        className="absolute left-0 top-0 h-full w-1 cursor-col-resize hover:bg-[#e8e7e4]"
        onMouseDown={onMouseDown}
      />

      <header className="flex items-center gap-2.5 border-b border-[#e8e7e4] px-4 py-3 bg-white">
        <span
          className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-[#8b5113] flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #fbeed8, #f4d8a4)" }}
        >
          <Bot className="h-3.5 w-3.5" />
        </span>
        <span className="text-[15px] font-semibold flex-1 truncate text-[#1d1f22]">
          Assistant
        </span>
        <Link
          to="/settings/ai-providers"
          className={cn(
            "flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium transition-colors",
            !hasAnyAuth &&
              "border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-400",
            hasAnyAuth &&
              hasOAuth &&
              "border-violet-300 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 dark:text-violet-400",
            hasAnyAuth &&
              !hasOAuth &&
              "border-[#e8e7e4] bg-white text-[#555861] hover:bg-[#f3f2ef]",
          )}
          title={
            hasOAuth
              ? `Compte Anthropic ${oauth.subscription_type ?? ""} connecté${
                  connectedProviders.length
                    ? ` + ${connectedProviders.length} clé(s) API`
                    : ""
                }`
              : connectedProviders.length
                ? `Clés actives: ${connectedProviders.map((p) => p.provider).join(", ")}`
                : "Aucune clé API configurée"
          }
        >
          {hasOAuth ? (
            <>
              <KeyRound className="h-3 w-3" />
              {oauth.subscription_type?.toUpperCase() ?? "OAUTH"}
            </>
          ) : connectedProviders.length ? (
            <>
              <KeyRound className="h-3 w-3" />
              {connectedProviders.length === 1
                ? connectedProviders[0].provider.toUpperCase()
                : `${connectedProviders.length} CLÉS`}
            </>
          ) : (
            <>
              <AlertTriangle className="h-3 w-3" />
              Configurer
            </>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-[#555861] hover:bg-[#f3f2ef] hover:text-[#1d1f22]"
          onClick={chat.newConversation}
          title="Nouvelle conversation"
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-[#555861] hover:bg-[#f3f2ef] hover:text-[#1d1f22]"
          onClick={() => setHistoryOpen((o) => !o)}
          title="Historique"
        >
          <History className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-[#555861] hover:bg-[#f3f2ef] hover:text-[#1d1f22]"
          onClick={() => setOpen(false)}
          title="Fermer (Ctrl+L)"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      {historyOpen && (
        <div className="border-b border-[#e8e7e4] bg-[#fbfaf7] max-h-60 overflow-auto">
          {items.length === 0 ? (
            <div className="p-3 text-xs text-[#8a8d96]">
              Aucune conversation
            </div>
          ) : (
            items.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  chat.loadConversation(c.id);
                  setHistoryOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-xs text-[#1d1f22] hover:bg-white truncate"
              >
                {c.title}
              </button>
            ))
          )}
        </div>
      )}

      {ctx?.entity_type && <ContextBar ctx={ctx} />}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 bg-[#fbfaf7]"
      >
        {chat.messages.length === 0 && (
          <EmptyState ctx={ctx} onPick={(t) => chat.send(t, ctx)} />
        )}
        <div className="flex flex-col gap-3.5">
          {chat.messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              onPrompt={(t) => chat.send(t, ctx)}
            />
          ))}
        </div>
        {chat.error && (
          <div className="mt-3 text-xs text-[#8b2725] bg-[#fbe5e5] border border-[#f0c8c6] rounded-lg p-2.5">
            {chat.error}
          </div>
        )}
      </div>

      <ChatInput
        status={chat.status}
        onSend={(t) => chat.send(t, ctx)}
        onStop={chat.stop}
      />
    </aside>
  );
}

function ContextBar({ ctx }: { ctx: ChatContext }) {
  const Icon =
    ctx.entity_type === "contact"
      ? User
      : ctx.entity_type === "company"
        ? Building2
        : ctx.entity_type === "deal"
          ? Briefcase
          : FileText;
  const label =
    ctx.entity_type === "contact"
      ? "Contact"
      : ctx.entity_type === "company"
        ? "Entreprise"
        : ctx.entity_type === "deal"
          ? "Deal"
          : "Page";
  return (
    <div className="flex items-center gap-1.5 px-4 py-1.5 border-b border-[#e8e7e4] text-[11.5px] text-[#8a8d96] bg-white">
      <Icon className="h-3 w-3" />
      <span>
        {label} #{ctx.entity_id}
      </span>
    </div>
  );
}

function EmptyState({
  ctx,
  onPick,
}: {
  ctx: ChatContext | undefined;
  onPick: (text: string) => void;
}) {
  const suggestions = [
    "Fais-moi le brief de la journée",
    "Quels deals je dois relancer ?",
    ...(ctx?.entity_type === "deal" ? ["Résume l'historique de ce deal"] : []),
    ...(ctx?.entity_type === "contact"
      ? ["Quels sont les derniers échanges ?"]
      : []),
    ...(ctx?.entity_type === "company" ? ["Synthèse de cette entreprise"] : []),
  ];
  return (
    <div className="text-sm text-[#555861]">
      <p className="font-semibold mb-1 text-[#1d1f22]">Bonjour 👋</p>
      <p className="text-[13px] leading-relaxed">
        Pose-moi une question sur tes contacts, deals, tâches… Je peux aussi
        créer des tâches ou des notes pour toi.
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="px-2.5 py-1 text-[12px] rounded-full border border-[#e8e7e4] bg-white text-[#555861] hover:bg-[#f3f2ef] hover:text-[#1d1f22] transition-colors"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onPrompt,
}: {
  message: ChatMessage;
  onPrompt?: (text: string) => void;
}) {
  const hasTools = (message.toolCalls?.length ?? 0) > 0;
  const isUser = message.role === "user";
  const showTyping = !isUser && message.pending && !message.content;

  return (
    <div
      className={cn(
        "flex gap-2",
        isUser ? "justify-end" : "justify-start items-start",
      )}
    >
      {!isUser && (
        <span
          className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[#8b5113] flex-shrink-0 mt-0.5"
          style={{ background: "linear-gradient(135deg, #fbeed8, #f4d8a4)" }}
        >
          <Bot className="h-3 w-3" />
        </span>
      )}
      <div
        className={cn(
          "flex flex-col gap-1 min-w-0",
          isUser ? "items-end max-w-[84%]" : "items-start max-w-[84%]",
        )}
      >
        {hasTools && !isUser && <ToolTimeline calls={message.toolCalls!} />}
        {showTyping ? (
          <div className="inline-flex items-center gap-1 px-3 py-3 rounded-xl rounded-bl-[4px] bg-white border border-[#e8e7e4]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#8a8d96] animate-[agent-bounce_1.2s_infinite]" />
            <span className="h-1.5 w-1.5 rounded-full bg-[#8a8d96] animate-[agent-bounce_1.2s_infinite] [animation-delay:0.15s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-[#8a8d96] animate-[agent-bounce_1.2s_infinite] [animation-delay:0.3s]" />
          </div>
        ) : (
          <div
            className={cn(
              "px-3 py-2 text-[13.5px] leading-relaxed border",
              isUser
                ? "rounded-xl rounded-br-[4px] bg-[#e2ebfa] border-[#c8d8f1] text-[#1d2c4d] whitespace-pre-wrap"
                : "rounded-xl rounded-bl-[4px] bg-white border-[#e8e7e4] text-[#1d1f22]",
            )}
          >
            {isUser ? (
              message.content || (message.pending ? "…" : "")
            ) : message.content ? (
              <MessageContent content={message.content} onPrompt={onPrompt} />
            ) : (
              ""
            )}
          </div>
        )}
      </div>
      {isUser && (
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#c9e7d8] text-[#0e6f4f] flex-shrink-0 mt-0.5">
          <User className="h-3 w-3" />
        </span>
      )}
    </div>
  );
}

function ChatInput({
  status,
  onSend,
  onStop,
}: {
  status: "idle" | "streaming" | "error";
  onSend: (t: string) => void;
  onStop: () => void;
}) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = () => {
    const t = value.trim();
    if (!t) return;
    onSend(t);
    setValue("");
  };

  return (
    <div className="border-t border-[#e8e7e4] bg-white p-3">
      <div className="flex items-end gap-2 border border-[#d6d4cf] rounded-[10px] bg-white px-3 py-2 focus-within:border-[#1d1f22] transition-colors">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Message… (Enter pour envoyer)"
          rows={1}
          className="flex-1 resize-none border-0 outline-none bg-transparent text-[13.5px] leading-[1.5] min-h-[20px] max-h-20 text-[#1d1f22] placeholder:text-[#8a8d96] disabled:opacity-50"
          disabled={status === "streaming"}
        />
        {status === "streaming" ? (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 flex-shrink-0 text-[#555861] hover:bg-[#f3f2ef] hover:text-[#1d1f22]"
            onClick={onStop}
            title="Stop"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <button
            onClick={submit}
            disabled={!value.trim()}
            title="Envoyer"
            className="flex h-7 w-7 items-center justify-center rounded-md bg-[#1c1d20] text-white hover:bg-[#2a2c30] disabled:opacity-40 disabled:hover:bg-[#1c1d20] flex-shrink-0 transition-colors"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
